import { styled } from "goober";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { playClick } from "../lib/sounds";
import { writeToClipboard } from "../lib/writeToClipboard";
import { AnimatedCopyIcon } from "./AnimatedCopyIcon";
import { Button } from "./Button";
import { ButtonLink } from "./Button/ButtonLink";
import { Dropdown } from "./Dropdown";
import { ToggleButton } from "./HeadingToggle";
import { Icon, type IconName } from "./Icon";
import { ShortcutHint } from "./ShortcutHint";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { UrlDisplay } from "./UrlDisplay";

export type BrowserLinkItem = {
  key: string;
  icon: IconName;
  label: string;
  metadata: string;
  selected: boolean;
  onClick: () => void;
};

type Props = {
  displayUrl: string;
  openUrl?: string;
  links?: ReadonlyArray<BrowserLinkItem>;
  onReload?: () => void;
  onBack?: () => void;
  onForward?: () => void;
  canBack?: boolean;
  canForward?: boolean;
  inspectMode?: boolean;
  onInspectModeChange?: (mode: boolean) => void;
  embedded?: boolean;
  access?: ReactNode;
  children: ReactNode;
};

const StyledBrowserChrome = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;

  &[data-embedded="true"] {
    border: none;
    border-radius: 0;
  }
`;

const Container = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 8px;
  flex-shrink: 0;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const UrlBar = styled("div")`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px;
  border-radius: 9999px;
  background: var(--alpha4);
  min-width: 0;
  box-sizing: border-box;

  &:hover {
    background: var(--alpha8);
  }

  &:hover [data-copy-link] {
    opacity: 1;
  }
`;

const UrlSlot = styled("div")`
  flex: 1;
  min-width: 0;
`;

const UrlText = styled("div")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  text-align: center;
  font-family: inherit;
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
`;

const CopyLinkTrigger = styled("button")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-right: -10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 0 9999px 9999px 0;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s ease-out,
    background 0.15s ease-out,
    color 0.15s ease-out;

  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }

  &[data-visible],
  &:focus-visible {
    opacity: 1;
  }
`;

const FrameWrap = styled("div")`
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  justify-content: center;
`;

// While a dropdown is open, the preview iframe would otherwise swallow clicks (an
// iframe never dispatches mousedown to the parent document), so clicking the page
// couldn't dismiss the menu. This transparent cover keeps that click in the parent
// document, where the dropdown's outside-click detection sees it.
const IframeCover = styled("div")`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const Viewport = styled("div")`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
`;

const InspectButton = styled(ToggleButton)`
  width: auto;
  gap: 6px;
  padding: 0 8px;

  &[aria-pressed="true"] {
    background: var(--alpha4);
  }
`;

function CopyLinkButton({ openUrl }: { openUrl: string }) {
  const [copied, setCopied] = useState(false);
  const copiedResetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => {
    return () => {
      if (copiedResetTimeout.current) clearTimeout(copiedResetTimeout.current);
    };
  }, []);

  const copyLink = () => {
    void writeToClipboard(openUrl);
    setCopied(true);
    if (copiedResetTimeout.current) clearTimeout(copiedResetTimeout.current);
    copiedResetTimeout.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <CopyLinkTrigger
      type="button"
      aria-label="Copy link"
      data-no-pill=""
      data-copy-link=""
      data-visible={copied ? "" : undefined}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        copyLink();
      }}
    >
      <AnimatedCopyIcon copied={copied} size={12} />
    </CopyLinkTrigger>
  );
}

export function BrowserChrome({
  displayUrl,
  openUrl,
  links,
  onReload,
  onBack,
  onForward,
  canBack,
  canForward,
  inspectMode,
  onInspectModeChange,
  embedded,
  access,
  children,
}: Props) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const urlBar = (
    <UrlBar
      onMouseDown={(e) => {
        if (e.button !== 0 || !e.metaKey || !openUrl) return;
        e.stopPropagation();
        playClick();
        window.open(openUrl, "_blank", "noopener,noreferrer");
      }}
    >
      {access}
      <UrlText>
        <UrlDisplay url={displayUrl} />
      </UrlText>
      {openUrl && <CopyLinkButton openUrl={openUrl} />}
    </UrlBar>
  );

  return (
    <StyledBrowserChrome data-embedded={embedded}>
      <Container>
        <Button
          variant="secondary"
          icon="arrow-left"
          type="button"
          onClick={onBack}
          disabled={!onBack || !canBack}
          aria-label="Back"
        />
        <Button
          variant="secondary"
          icon="arrow-right"
          type="button"
          onClick={onForward}
          disabled={!onForward || !canForward}
          aria-label="Forward"
        />
        <Button
          variant="secondary"
          icon="rotate-cw"
          type="button"
          onClick={onReload}
          disabled={!onReload}
          aria-label="Reload"
        />
        <UrlSlot>
          {links && links.length > 0 ? (
            <Dropdown
              align="bottom-center"
              minWidth={320}
              margin="6px 0 0"
              onOpenChange={setDropdownOpen}
              opener={urlBar}
              sections={[
                {
                  items: links.map((link) => ({
                    type: "button" as const,
                    key: link.key,
                    selected: link.selected,
                    left: <Icon name={link.icon} size={16} />,
                    label: link.label,
                    subtitle: (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {link.metadata}
                      </Text>
                    ),
                    right: link.selected ? (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    ) : null,
                    onClick: link.onClick,
                  })),
                },
              ]}
            />
          ) : (
            urlBar
          )}
        </UrlSlot>
        {openUrl && (
          <Tooltip content="Open in new tab">
            <ButtonLink
              variant="secondary"
              icon="arrow-up-right"
              to={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in new tab"
            />
          </Tooltip>
        )}
        <Tooltip
          content={
            <>
              Point and prompt <ShortcutHint>⇧⌥I</ShortcutHint>
            </>
          }
        >
          <InspectButton
            aria-pressed={!!inspectMode}
            aria-label="Toggle inspect"
            disabled={!onInspectModeChange}
            onPress={() => onInspectModeChange?.(!inspectMode)}
          >
            <Icon name="mouse-pointer-2" size={12} />
            <Text variant="text-small-md">Inspect</Text>
          </InspectButton>
        </Tooltip>
      </Container>
      <FrameWrap>
        <Viewport>{children}</Viewport>
        {dropdownOpen && <IframeCover />}
      </FrameWrap>
    </StyledBrowserChrome>
  );
}
